import { useState, useEffect, useCallback } from 'react'
import { supabase } from '../../../../lib/supabase'

interface Conversation {
    id: string
    title: string
    agent_id: string | null
    created_at: string
    updated_at: string
}

/**
 * Hook to manage RAG conversations
 * Clone of useChatConversations but specialized for RAG interactions
 */
export const useRagConversations = (
    userId: string | null,
    // Optional: filter by specific RAG agent ID if we ever have multiple RAG personas
    ragAgentId: string | null = 'RAG_SYSTEM'
) => {
    const [conversations, setConversations] = useState<Conversation[]>([])
    const [currentConversationId, setCurrentConversationId] = useState<string | null>(null)
    const [isLoading, setIsLoading] = useState(false)

    // Load conversations for current user
    const loadConversations = useCallback(async () => {
        if (!userId) return

        setIsLoading(true)
        // Clear state immediately to prevent stale data
        setConversations([])
        setCurrentConversationId(null)

        try {
            // For now, we store RAG conversations in the SAME table 'conversations'.
            // To distinguish them from normal Agent chats, we must decide on a convention.
            // Convention: agent_id = 'RAG_SYSTEM' (or whatever value we pass)

            let query = supabase
                .from('conversations')
                .select('*')
                .eq('user_id', userId)
                .order('updated_at', { ascending: false })

            if (ragAgentId && ragAgentId !== 'RAG_SYSTEM') {
                query = query.eq('agent_id', ragAgentId)
            } else if (ragAgentId === 'RAG_SYSTEM') {
                // RAG System conversations have no specific Agent UUID (null)
                query = query.is('agent_id', null)
            } else {
                // If no ID provided, maybe fetch all? Or nulls? 
                // For safety, let's require an ID for RAG to keep it separate.
                console.warn('[useRagConversations] No ragAgentId provided, returning empty.')
                setConversations([])
                return
            }

            const { data, error } = await query

            if (error) throw error

            const newConversations = (data || []) as unknown as Conversation[]
            setConversations(newConversations)

            // Auto-select latest conversation or validate current
            setCurrentConversationId(prev => {
                const isValid = newConversations.find(c => c.id === prev)
                if (isValid) return prev
                return newConversations.length > 0 ? newConversations[0].id : null
            })
        } catch (err) {
            console.error('[useRagConversations] Error loading conversations:', err)
        } finally {
            setIsLoading(false)
        }
    }, [userId, ragAgentId])

    // Load conversations when user changes
    useEffect(() => {
        loadConversations()
    }, [loadConversations])

    // Create a new conversation
    const createConversation = useCallback(async (title?: string) => {
        if (!userId) return null

        try {
            const newConversation = {
                user_id: userId,
                // If RAG_SYSTEM, store as null (system conversation)
                agent_id: ragAgentId === 'RAG_SYSTEM' ? null : ragAgentId,
                title: title || 'Nova Busca RAG',
                created_at: new Date().toISOString(),
                updated_at: new Date().toISOString()
            }

            const { data, error } = await supabase
                .from('conversations')
                .insert(newConversation)
                .select()
                .single()

            if (error) throw error

            setConversations(prev => [data as unknown as Conversation, ...prev])
            setCurrentConversationId(data.id)

            return data as unknown as Conversation
        } catch (err) {
            console.error('[useRagConversations] Error creating conversation:', err)
            return null
        }
    }, [userId, ragAgentId])

    // Switch to a different conversation
    const switchConversation = useCallback((conversationId: string) => {
        setCurrentConversationId(conversationId)
    }, [])

    // Delete a conversation
    const deleteConversation = useCallback(async (conversationId: string) => {
        try {
            const { error } = await supabase
                .from('conversations')
                .delete()
                .eq('id', conversationId)

            if (error) throw error

            setConversations(prev => prev.filter(c => c.id !== conversationId))

            if (currentConversationId === conversationId) {
                setCurrentConversationId(null)
            }
        } catch (err) {
            console.error('[useRagConversations] Error deleting conversation:', err)
        }
    }, [currentConversationId])

    return {
        conversations,
        currentConversationId,
        isLoading,
        loadConversations,
        createConversation,
        switchConversation,
        deleteConversation
    }
}
